import type { Product, ProductFeature, ProductFeatures } from '@entities/product'

export type ProductStepId =
  | 'basico'
  | 'recursos'
  | 'preco'
  | 'funnel'
  | 'tracking'
  | 'global'
  | 'afiliados'
  | 'cupons'
  | 'coproducao'
  | 'email'
  | 'links'

export type ProductStep = {
  id: ProductStepId
  title: string
  description: string
  feature?: ProductFeature
  recommended?: boolean
}

export type FeatureDefinition = {
  key: ProductFeature
  step: ProductStepId
  title: string
  description: string
  badge?: string
  recommended?: boolean
}

export const featureDefinitions: FeatureDefinition[] = [
  {
    key: 'funnel',
    step: 'funnel',
    title: 'Ofertas & Funil de Vendas',
    description: 'Order bump, upsell e downsell para aumentar ticket.',
    badge: 'Recomendado',
    recommended: true,
  },
  {
    key: 'tracking',
    step: 'tracking',
    title: 'Trackeamento & Pixels',
    description: 'Meta, TikTok, Google Ads e UTMify para medir conversão.',
    badge: 'Avançado',
  },
  {
    key: 'affiliates',
    step: 'afiliados',
    title: 'Programa de Afiliados',
    description: 'Comissão, aprovação e link de convite.',
    badge: 'Recomendado',
  },
  {
    key: 'coupons',
    step: 'cupons',
    title: 'Cupons de Desconto',
    description: 'Códigos promocionais percentuais ou fixos.',
  },
  {
    key: 'coproduction',
    step: 'coproducao',
    title: 'Co-produção',
    description: 'Divisão de receita com um parceiro.',
  },
  {
    key: 'email',
    step: 'email',
    title: 'E-mail de Compra',
    description: 'Mensagem pós-venda com variáveis.',
  },
]

export const allProductSteps: ProductStep[] = [
  { id: 'basico', title: 'Básico', description: 'Nome, capa e suporte' },
  { id: 'recursos', title: 'Recursos', description: 'Escolha o que configurar' },
  { id: 'preco', title: 'Preço', description: 'Valor e pagamento' },
  {
    id: 'funnel',
    title: 'Funil',
    description: 'Bump, upsell e downsell',
    feature: 'funnel',
    recommended: true,
  },
  {
    id: 'tracking',
    title: 'Trackeamento',
    description: 'Pixels e conversão',
    feature: 'tracking',
  },
  {
    id: 'afiliados',
    title: 'Afiliados',
    description: 'Comissão e aprovação',
    feature: 'affiliates',
  },
  { id: 'cupons', title: 'Cupons', description: 'Códigos de desconto', feature: 'coupons' },
  {
    id: 'coproducao',
    title: 'Co-produção',
    description: 'Parceiro e percentual',
    feature: 'coproduction',
  },
  { id: 'email', title: 'E-mail', description: 'Template pós-venda', feature: 'email' },
  { id: 'links', title: 'Links', description: 'Publicar e divulgar' },
]

export function isStepVisible(step: ProductStep, features: ProductFeatures): boolean {
  return step.feature === undefined || features[step.feature]
}

export function fallbackStepForHidden(
  activeStep: ProductStepId,
  features: ProductFeatures,
): ProductStepId {
  const step = allProductSteps.find((item) => item.id === activeStep)
  if (step && isStepVisible(step, features)) {
    return activeStep
  }
  return 'recursos'
}

export function getStepTag(product: Product, stepId: ProductStepId): string | undefined {
  if (stepId === 'basico') {
    return product.name.trim() && product.productType && product.category.trim()
      ? 'Configurado'
      : 'Pendente'
  }
  if (stepId === 'recursos') {
    return Object.values(product.features).some(Boolean) ? 'Configurado' : 'Pendente'
  }
  if (stepId === 'preco') return Number(product.price.amount) > 0 ? 'Configurado' : 'Pendente'
  if (stepId === 'funnel') {
    const total = product.orderBumps.length + product.funnelNodes.length
    if (total === 0) return 'Pendente'
    return product.orderBumps.length > 0 && product.funnelNodes.length > 0
      ? 'Configurado'
      : 'Incompleto'
  }
  if (stepId === 'tracking') return product.pixels.length > 0 ? 'Configurado' : 'Pendente'
  if (stepId === 'afiliados') return product.affiliate.enabled ? 'Configurado' : 'Pendente'
  if (stepId === 'cupons') return product.coupons.length > 0 ? 'Configurado' : 'Pendente'
  if (stepId === 'coproducao') return product.coproducers.length > 0 ? 'Configurado' : 'Pendente'
  if (stepId === 'email') return product.emailTemplate.active ? 'Configurado' : 'Pendente'
  return undefined
}

export function getStepStatus(tagLabel: string | undefined, active: boolean) {
  if (active) return 'current' as const
  if (tagLabel === 'Configurado') return 'complete' as const
  if (tagLabel === 'Incompleto') return 'incomplete' as const
  return 'pending' as const
}
